"use client";

import { InterestGroup } from "../../../app/storyblok/[localeString]/support-hub/types/contact.types";
// import { useSearchActive } from "../../../helpers/SearchActiveContext";
import { analyticsFilter } from "../../../helpers/analytics";
import { FilterPanel } from "../FilterPanel/FilterPanel";
import InterestsGroups from "../InterestsGroups/InterestsGroups";
import type { TypesenseSearchFieldProps } from "../TypesenseSearchField/TypesenseSearchField";
import { TypesenseSearchField } from "../TypesenseSearchField/TypesenseSearchField";
import { useSideMenuLogic } from "./useSideMenuLogic";
import { CosmosIconFilter, CosmosText, CosmosTitle } from "@cosmos/web-react";
import classNames from "classnames/bind";
import styles from "./DesktopMenu.module.css";

const cx = classNames.bind(styles);

export function DesktopMenu({
  pageTitle,
  pageIntro,
  interestGroupsLabel,
  interestGroups,
  categoriesLabel,
  topicsLabel,
  filterPanelClearAllLabel,
  resultsLabel,
  typesenseSearch,
}: {
  pageTitle: string;
  pageIntro?: React.ReactNode;
  interestGroupsLabel: string;
  interestGroups: InterestGroup[];
  categoriesLabel: string;
  topicsLabel: string;
  filterPanelClearAllLabel: string;
  resultsLabel: string;
  typesenseSearch?: TypesenseSearchFieldProps;
}) {
  // const { isSearchActive } = useSearchActive();
  const {
    selectedInterestGroup,
    availableCategories,
    selectedCategoriesIds,
    availableTopics,
    isTopicsDisabled,
    selectedTopicsIds,
    totalResultsCount,
    contactPageRouter,
    trackClick,
  } = useSideMenuLogic({
    interestGroups,
  });

  const categoriesFilterPanel = (
    <FilterPanel
      key={selectedInterestGroup?.uuid || "none"}
      clearAllLabel={filterPanelClearAllLabel}
      items={availableCategories.map((category) => ({
        key: category.uuid,
        title: category.title,
        selected: selectedCategoriesIds.includes(category.uuid),
        href: contactPageRouter.toggleCategoryUrl({ uuid: category.uuid }),
        onClick: () => {
          trackClick();
          analyticsFilter({
            value: category.title,
            contentType: "filter",
          });
        },
      }))}
      label={categoriesLabel}
      deselectAllHref={contactPageRouter.deselectAllCategoriesUrl()}
    />
  );

  const topicsFilterPanel = (
    <FilterPanel
      buttonShape="rectangle"
      key={selectedCategoriesIds.join("-") || "none"}
      clearAllLabel={filterPanelClearAllLabel}
      disabled={isTopicsDisabled}
      selected={isTopicsDisabled ? false : undefined}
      items={availableTopics.map((topic) => {
        const selected = selectedTopicsIds.includes(topic.uuid);
        return {
          key: topic.uuid,
          title: topic.topicName,
          selected,
          href: contactPageRouter.toggleTopicUrl({ uuid: topic.uuid }),
          onClick: () => {
            trackClick();
            contactPageRouter.toggleTopicUrl({
              uuid: topic.uuid,
            });
            analyticsFilter({
              value: topic.topicName,
              contentType: "filter",
            });
          },
        };
      })}
      label={topicsLabel}
      deselectAllHref={contactPageRouter.deselectAllTopicsUrl()}
    />
  );

  return (
    // TODO: Re-enable inert state when search is active. Temporarily disabled
    // because the side menu can get stuck inert after closing a question modal
    // that was opened from a search result. Revisit later.
    <div className={cx("container")} /* inert={isSearchActive} */>
      {/* <div className={cx("inert-when-search")}> */}
      <div className={cx("header")}>
        <div
          className={cx("header-expander")}
          data-hidden={!!selectedInterestGroup}
        >
          <div className={cx("header-expander-inner")}>
            <div className={cx("header-content")}>
              <div className={cx("header-title-inner")}>
                <CosmosTitle
                  tag="h1"
                  size="large"
                  appearance="dark"
                  weight="bold"
                  className={cx("title")}
                >
                  {pageTitle}
                </CosmosTitle>

                {/* TODO: Prose to allow for links */}
                {pageIntro && (
                  <CosmosText
                    tag="div"
                    size="small"
                    appearance="dark"
                    kind="subtle"
                  >
                    {pageIntro}
                  </CosmosText>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* </div> */}

      {/* <div className={cx("inert-when-search")} inert={isSearchActive}> */}

      <div className={cx("nav")}>
        {typesenseSearch && (
          <div className={cx("search")}>
            <TypesenseSearchField {...typesenseSearch} />
          </div>
        )}
        <CosmosText
          tag="p"
          appearance="dark"
          size="small"
          weight="medium"
          kind="normal"
          className={cx("header-interest-groups-label")}
          aria-hidden={true}
        >
          {interestGroupsLabel}
        </CosmosText>
        {/* Because of animation we duplicate the interestGroupsLabel on desktop */}
        <InterestsGroups
          interestGroupsLabel={interestGroupsLabel}
          interests={interestGroups}
          dragProgress={0}
          hasMobileCarousel={true}
          appearance="dark"
          hideTitle={true}
        />
        {selectedInterestGroup && (
          <div
            className={cx("filter-panel-wrap")}
            // Search results supersede category/topic filtering, so the
            // panel is non-interactive while `?q=...` is active.
            inert={Boolean(contactPageRouter.selectedSearchQuery) || undefined}
          >
            <CosmosText
              className={cx("filter-panel-title")}
              tag="h2"
              appearance="dark"
              weight="medium"
              size="small"
            >
              <CosmosIconFilter className={cx("filter-panel-title-icon")} />
              {totalResultsCount} {resultsLabel || "Results"}
            </CosmosText>

            <div className={cx("filter-panels")}>
              {availableCategories.length && categoriesFilterPanel}
              {availableCategories.length && topicsFilterPanel}
            </div>
          </div>
        )}
      </div>
    </div>
    // </div>
  );
}
